import { Directive, HostListener, inject, Renderer2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { ANALYTICS_FALLBACK_USER_NAME } from '@shared';
import { environment } from '../../../environments/environment';
import * as fromAuth from '../../auth';

interface AtlJqPageProps {
  triggerFunction(showCollectorDialog: () => void): void;
  fieldValues: {
    email: string;
    fullname: string;
    [x: string]: string;
  };
}

declare global {
  interface Window {
    ATL_JQ_PAGE_PROPS?: AtlJqPageProps;
  }
}

@Directive({
  selector: '[squadboxJiraFeedback]',
  standalone: true,
})
export class JiraFeedbackDirective {
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly renderer = inject(Renderer2);

  public readonly user = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );

  @HostListener('click')
  public handleClickReportCTA() {
    // While not ideal, adding the issue collector script programmatically in the component rather than
    // having it static in the index.html is the only method we found that was allowing us to populate
    // the field values dynamically, which is needed to assign the reporter automatically through a
    // JIRA automation.
    this.loadJiraCollector();
  }

  private loadJiraCollector() {
    const user = this.user();
    const contactEmail = user?.email ?? '';
    const fullName = user?.fullName ?? ANALYTICS_FALLBACK_USER_NAME;
    const scriptId = 'jira-collector-script';
    const triggerEventName = 'click-jira-report';

    // If the script is already in the DOM, we don't need to insert it again.
    // We simply need to open the issue collector dialog by dispatching our trigger event.
    if (document.getElementById(scriptId)) {
      window.dispatchEvent(new Event(triggerEventName));
      return;
    }

    const params = new URLSearchParams({
      collectorId: environment.jiraIssueCollector.collectorId,
    });

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    this.renderer.setAttribute(script, 'id', scriptId);
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setAttribute(
      script,
      'src',
      `${environment.jiraIssueCollector.scriptBaseUrl}?${params.toString()}`,
    );

    const clientEmailCustomFieldKey = `customfield_${environment.jiraIssueCollector.customFieldIds.clientEmail}`;
    const clientNameCustomFieldKey = `customfield_${environment.jiraIssueCollector.customFieldIds.clientName}`;

    script.onload = () => {
      window.ATL_JQ_PAGE_PROPS = {
        triggerFunction: function (showCollectorDialog: () => void) {
          window.addEventListener(triggerEventName, () => {
            showCollectorDialog();
          });
        },
        // Populating field values dynamically
        fieldValues: {
          email: contactEmail,
          fullname: fullName,
          // Custom fields will be used by JIRA automation to automatically create the customer if doesn't already exists
          // and assign them as reporter.
          // They are hidden fields that don't show in the issue collector dialog, so the user cannot update them via the UI,
          // unlike other fields including email.
          [clientNameCustomFieldKey]: fullName,
          [clientEmailCustomFieldKey]: contactEmail,
        },
      };

      // Wait a little bit before dispatching the trigger event (delay is arbitrary)
      setTimeout(() => {
        window.dispatchEvent(new Event(triggerEventName));
      }, 300);
    };

    this.renderer.appendChild(document.head, script);
  }
}
